import React, { useEffect, useRef, useState } from "react";
import "./TableSelect.css";

type TableSelectOption = {
  value: number;
  label: string;
};

type TableSelectProps = {
  value: number;
  options: TableSelectOption[];
  onChange: (value: number) => void;
  disabled?: boolean;
  id?: string;
  ariaLabel?: string;
};

const TableSelect: React.FC<TableSelectProps> = ({
  value,
  options,
  onChange,
  disabled = false,
  id = "table-select",
  ariaLabel = "Selecionar valor",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selected = options.find((o) => o.value === value) ?? options[0];

  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsOpen((prev) => !prev);
    }
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <div
      ref={containerRef}
      id={id}
      className={`table-select ${isOpen ? "table-select--open" : ""} ${disabled ? "table-select--disabled" : ""}`}
    >
      <button
        type="button"
        className="table-select__trigger"
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        onKeyDown={onTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        disabled={disabled}
      >
        <span className="table-select__value">{selected?.label ?? String(value)}</span>
        <span className="table-select__chevron" aria-hidden>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>

      {isOpen && (
        <ul className="table-select__dropdown" role="listbox" aria-label={ariaLabel}>
          {options.map((opt) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              className={`table-select__option ${opt.value === value ? "table-select__option--selected" : ""}`}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onChange(opt.value);
                  setIsOpen(false);
                }
              }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TableSelect;
